import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableDiff} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {ProcessTaskTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';

const DATA_MODS_DISABLED_COLUMNS = [
  TableColumnType.PROCESS_TASK_STATUS,
  TableColumnType.PROCESS_TASK_ASSIGNEE,
  TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE,
];

/**
 * Given structuredTables and tableSubmissionUpdates, this function returns the count of cell differences from
 * structuredTables that do not include changes to disabled columns or to unfinalized (never submitted) rows.
 *
 * If the structuredTables are of type PROCESS_TASK then return the task apiIds per modified row; however,
 * if the structuredTables are of type PROCESS_OUTPUT then return the output apiIds per modified row.
 */
export default function getDataModsCountForTableSubmission(
  structuredTables: Array<UnknownStructuredTable>,
  tableSubmissionUpdates: Array<StructuredTableDiff>
): {
  [k: string]: Array<string>;
} {
  assert(structuredTables.length > 0, 'No structured tables found.');
  const itemChangeCounts: {[k: string]: Array<string>} = {};
  if (!BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_WORKFLOWS) {
    return itemChangeCounts;
  }

  const rowIds = getProcessStructuredTableDiffsRowIds(tableSubmissionUpdates, structuredTables).flat();
  const rows = structuredTables.flatMap((table) => table.rows);

  const apiIds: Array<string> = [];
  for (const rowId of rowIds) {
    const rowFilter = rows.filter((row) => row.rowId === rowId);
    assert(rowFilter.length === 1, 'Unique row associated to table submission updates not found.');
    const row = rowFilter[0];
    if (structuredTables[0].kind === StructuredTableKind.PROCESS_TASK) {
      const taskCellFilter = row.cells.filter(
        (cell) => cell.typedValue && cell.typedValue.type === BaseTypedValueType.PROCESS_TASK
      );
      assert(taskCellFilter.length === 1, 'Row must have exactly one task cell.');
      const taskCell = taskCellFilter[0];
      apiIds.push((taskCell.typedValue as ProcessTaskTypedValue).id);
    } else if (structuredTables[0].kind === StructuredTableKind.PROCESS_OUTPUT) {
      const taskCellFilter = row.cells.filter(
        (cell) => cell.typedValue && cell.typedValue.type === BaseTypedValueType.PROCESS_OUTPUT
      );
      assert(taskCellFilter.length === 1, 'Row must have exactly one output cell.');
      const taskCell = taskCellFilter[0];
      apiIds.push((taskCell.typedValue as ProcessTaskTypedValue).id);
    } else {
      throw new Error(
        `Structured tables must be of type ${StructuredTableKind.PROCESS_TASK} or ${StructuredTableKind.PROCESS_OUTPUT}.`
      );
    }
  }
  if (apiIds.length === 0) {
    return itemChangeCounts;
  }
  itemChangeCounts.cell = apiIds;
  return itemChangeCounts;
}

function getProcessStructuredTableDiffsRowIds(
  tableDiffs: Array<StructuredTableDiff>,
  structuredTables: Array<UnknownStructuredTable>
): Array<Array<string>> {
  const columnIdToColumnType: {[k: string]: TableColumnType} = {};
  for (const structuredTable of structuredTables) {
    for (const column of structuredTable.columns) {
      columnIdToColumnType[column.provenanceUuid] = column.columnType;
    }
  }

  return tableDiffs
    .map((tableDiff) =>
      tableDiff.rowDiffs.map((row) =>
        row.isFinalized
          ? Array<string>(
              row.cellDiffs.filter(
                (diff) =>
                  !DATA_MODS_DISABLED_COLUMNS.includes(columnIdToColumnType[diff.columnProvenanceUuid])
              ).length
            ).fill(row.rowId)
          : []
      )
    )
    .flat();
}
